import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import Axios, { authHeaders } from "../../utils/axios";

export const fetchProfileSuggestionCards = createAsyncThunk(
    "profileSuggestionCards/fetchList",
    async ({ search = "", page = 1, limit = 10 }, { rejectWithValue }) => {
        try {
            const res = await Axios.get(
                `/profile-suggestion-cards?search=${search}&page=${page}&limit=${limit}`,
                authHeaders()
            );
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const fetchProfileSuggestionCardById = createAsyncThunk(
    "profileSuggestionCards/fetchById",
    async (id, { rejectWithValue }) => {
        try {
            const res = await Axios.get(`/profile-suggestion-cards/${id}`, authHeaders());
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const createProfileSuggestionCard = createAsyncThunk(
    "profileSuggestionCards/create",
    async (formData, { rejectWithValue }) => {
        try {
            const auth = authHeaders();
            const res = await Axios.post("/profile-suggestion-cards", formData, {
                headers: {
                    ...auth.headers,
                    "Content-Type": "multipart/form-data",
                },
            });
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const updateProfileSuggestionCard = createAsyncThunk(
    "profileSuggestionCards/update",
    async ({ id, formData }, { rejectWithValue }) => {
        try {
            const auth = authHeaders();
            const res = await Axios.patch(`/profile-suggestion-cards/${id}`, formData, {
                headers: {
                    ...auth.headers,
                    "Content-Type": "multipart/form-data",
                },
            });
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const deleteProfileSuggestionCard = createAsyncThunk(
    "profileSuggestionCards/delete",
    async (id, { rejectWithValue }) => {
        try {
            const res = await Axios.delete(`/profile-suggestion-cards/${id}`, authHeaders());
            return { id, res: res.data };
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const toggleProfileSuggestionCardStatus = createAsyncThunk(
    "profileSuggestionCards/toggleStatus",
    async ({ id, is_active }, { rejectWithValue }) => {
        try {
            const res = await Axios.patch(
                `/profile-suggestion-cards/${id}/status`,
                { is_active },
                authHeaders()
            );
            return { id, is_active, res: res.data };
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const reorderProfileSuggestionCard = createAsyncThunk(
    "profileSuggestionCards/reorder",
    async ({ id, order }, { rejectWithValue }) => {
        try {
            const res = await Axios.patch(
                `/profile-suggestion-cards/${id}/reorder`,
                { order },
                authHeaders()
            );
            return { id, order, res: res.data };
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const fetchAvailableSuggestionKeys = createAsyncThunk(
    "profileSuggestionCards/fetchAvailableKeys",
    async (_, { rejectWithValue }) => {
        try {
            const res = await Axios.get("/profile-suggestion-cards/config/keys", authHeaders());
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

const profileSuggestionCardSlice = createSlice({
    name: "profileSuggestionCards",
    initialState: {
        list: [],
        currentCard: null,
        total: 0,
        isLoading: false,
        error: null,
    },
    reducers: {
        clearCurrentCard: (state) => {
            state.currentCard = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchProfileSuggestionCards.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchProfileSuggestionCards.fulfilled, (state, action) => {
                state.isLoading = false;
                state.list = action.payload?.data?.data || [];
                state.total = action.payload?.data?.pagination?.total || 0;
            })
            .addCase(fetchProfileSuggestionCards.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            .addCase(fetchProfileSuggestionCardById.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchProfileSuggestionCardById.fulfilled, (state, action) => {
                state.isLoading = false;
                state.currentCard = action.payload?.data || null;
            })
            .addCase(fetchProfileSuggestionCardById.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            .addCase(deleteProfileSuggestionCard.fulfilled, (state, action) => {
                state.list = state.list.filter((item) => item.id !== action.payload.id);
                state.total -= 1;
            })
            .addCase(toggleProfileSuggestionCardStatus.fulfilled, (state, action) => {
                const { id, is_active } = action.payload;
                const card = state.list.find((item) => item.id === id);
                if (card) {
                    card.is_active = is_active;
                }
            })
            .addCase(reorderProfileSuggestionCard.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(reorderProfileSuggestionCard.fulfilled, (state, action) => {
                state.isLoading = false;
                const { id, order } = action.payload;
                const card = state.list.find((item) => item.id === id);
                if (card) {
                    card.order = order;
                }
            })
            .addCase(reorderProfileSuggestionCard.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            });
    },
});

export const { clearCurrentCard } = profileSuggestionCardSlice.actions;
export default profileSuggestionCardSlice.reducer;
